/** A step's CONTRACT in document mode (E3, spec 2026-09-18 §6 Edit): the
 * rows Customize renders locked — lane, executor type, needs, produces,
 * offered levels, the timeout — editable here because a custom row's author
 * owns the whole document. Every write is a whole-key patch on the step;
 * the executor block re-renders for its type. Maps this form has no editor
 * for (`args`, `output`, `queue`) stay reachable on the Raw tab. */
import { Input, NativeSelect, TagInput } from '@/components/ui'
import type {
  ProcessExecutorJson,
  ProcessLevel,
  ProcessStepJson,
  ProcessWorkflowDefinitionJson,
} from '@/types/workflowDefinition'
import { ALLOWED_LEVELS, elapsedExit, executorSkeleton, executorTypesFor, failedExit, levelSkeleton, type ExecutorType } from './stepSkeleton'

type WaitExecutor = Extract<ProcessExecutorJson, { type: 'wait' }>
const WAIT_SURFACES = ['record.feasibility', 'record.quotes']

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1 text-xs font-medium text-foreground">
      <span>{label}</span>
      {children}
    </label>
  )
}

export function ProcessStepContractEditor({
  stepId,
  doc,
  onChange,
}: {
  stepId: string
  doc: ProcessWorkflowDefinitionJson
  onChange: (stepId: string, patch: Partial<ProcessStepJson>) => void
}) {
  const step = doc.steps[stepId]
  const executor = step.executor
  const fieldIds = Object.keys(doc.fields)
  const patch = (p: Partial<ProcessStepJson>) => onChange(stepId, p)
  const setExecutor = (next: ProcessExecutorJson) => patch({ executor: next })
  const allowed = ALLOWED_LEVELS[executor.type]
  const offered = step.level?.offered ?? []
  const timeout = step.timeout ?? null
  // `!= null`, not truthiness: a field-armed timeout with no field chosen yet
  // is still field-armed (the published dump carries the unused key as null).
  const timeoutMode = !timeout ? 'none' : timeout.from_field != null ? 'from_field' : 'after'
  const exitNames = Object.keys(step.exits ?? {})
  const datetimeFields = fieldIds.filter((f) => doc.fields[f]?.type === 'datetime')

  function retype(type: ExecutorType) {
    if (type === executor.type) return
    // The level rides the type: a person step is manual-only, a wait has none.
    // So does an automation's `failed` exit — the server refuses one without it.
    patch({
      executor: executorSkeleton(type, doc, executor),
      level: levelSkeleton(type),
      ...(type === 'automation' && !('failed' in (step.exits ?? {}))
        ? { exits: { ...step.exits, failed: failedExit(doc) } }
        : {}),
    })
  }
  function toggleLevel(level: ProcessLevel, on: boolean) {
    const next = allowed.filter((l) => (l === level ? on : offered.includes(l)))
    if (next.length === 0) return // `offered` is never empty (LevelSpec min_length=1)
    const current = step.level?.default
    patch({ level: { default: current && next.includes(current) ? current : next[0], offered: next } })
  }
  function setTimeoutMode(mode: string) {
    if (mode === 'none') return patch({ timeout: undefined })
    const exit = timeout?.exit ?? exitNames[0] ?? ''
    if (mode === 'after') return patch({ timeout: { after: timeout?.after ?? '1d', calendar: timeout?.calendar ?? 'clock', exit } })
    patch({ timeout: { from_field: timeout?.from_field ?? datetimeFields[0] ?? '', exit } })
  }
  function setWait(next: Partial<WaitExecutor>) {
    if (executor.type === 'wait') setExecutor({ ...executor, ...next })
  }

  return (
    <fieldset className="space-y-3 rounded-xl border border-border/60 p-3">
      <legend className="px-1 text-xs font-semibold text-foreground">Contract</legend>
      <div className="grid gap-3 sm:grid-cols-2">
        <Row label="Lane">
          <NativeSelect aria-label="Lane" value={step.lane} onChange={(e) => patch({ lane: e.target.value })}>
            {!(step.lane in doc.lanes) && <option value={step.lane}>{step.lane || '— none —'}</option>}
            {Object.entries(doc.lanes).map(([id, lane]) => (
              <option key={id} value={id}>
                {lane.label?.en ?? id}
              </option>
            ))}
          </NativeSelect>
        </Row>
        <Row label="Who works it">
          <NativeSelect aria-label="Who works it" value={executor.type} onChange={(e) => retype(e.target.value as ExecutorType)}>
            {executorTypesFor(doc.entry === stepId, executor.type).map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </NativeSelect>
        </Row>

        {executor.type === 'automation' && (
          <Row label="Capability">
            <Input
              aria-label="Capability"
              placeholder="e.g. crm.lookup_company"
              value={executor.capability}
              onChange={(e) => setExecutor({ ...executor, capability: e.target.value })}
            />
          </Row>
        )}
        {executor.type === 'conversation' && (
          <>
            <Row label="Agenda">
              <Input
                aria-label="Agenda"
                value={executor.agenda}
                onChange={(e) => setExecutor({ ...executor, agenda: e.target.value })}
              />
            </Row>
            <Row label="Conversation with">
              <NativeSelect
                aria-label="Conversation with"
                value={executor.party ?? 'staff'}
                onChange={(e) => setExecutor({ ...executor, party: e.target.value as 'staff' | 'customer' })}
              >
                <option value="staff">staff</option>
                <option value="customer">customer</option>
              </NativeSelect>
            </Row>
          </>
        )}
        {executor.type === 'wait' && (
          <>
            <Row label="Waits for">
              <NativeSelect
                aria-label="Waits for"
                value={'event' in executor.on ? 'event' : 'after'}
                onChange={(e) => {
                  if (e.target.value === 'event') return setWait({ on: { event: '' } })
                  // A time wait leaves by `elapsed` — the server refuses one without it.
                  patch({
                    executor: { ...executor, on: { after: '1d', calendar: 'clock' } },
                    ...('elapsed' in (step.exits ?? {}) ? {} : { exits: { ...step.exits, elapsed: elapsedExit(doc, stepId) } }),
                  })
                }}
              >
                <option value="event">an event on the record</option>
                <option value="after">an amount of time</option>
              </NativeSelect>
            </Row>
            {'event' in executor.on ? (
              <>
                <Row label="Event name">
                  <Input
                    aria-label="Event name"
                    placeholder="e.g. quote_version_sent"
                    value={executor.on.event}
                    onChange={(e) => setWait({ on: { ...executor.on, event: e.target.value } as WaitExecutor['on'] })}
                  />
                </Row>
                <Row label="Exit named by payload key">
                  <Input
                    aria-label="Exit named by payload key"
                    placeholder="leave empty for a single exit"
                    value={executor.on.exit_from ?? ''}
                    onChange={(e) => {
                      const { exit_from: _drop, ...rest } = executor.on as { event: string; exit_from?: string | null }
                      setWait({ on: (e.target.value ? { ...rest, exit_from: e.target.value } : rest) as WaitExecutor['on'] })
                    }}
                  />
                </Row>
              </>
            ) : (
              <Row label="Wait for">
                <Input
                  aria-label="Wait for"
                  placeholder="e.g. 4h, 1d"
                  value={executor.on.after}
                  onChange={(e) => setWait({ on: { ...executor.on, after: e.target.value } as WaitExecutor['on'] })}
                />
              </Row>
            )}
            <Row label="Task links to">
              <NativeSelect
                aria-label="Task links to"
                value={executor.surface ?? ''}
                onChange={(e) => {
                  const { surface: _drop, ...rest } = executor
                  setExecutor(e.target.value ? { ...rest, surface: e.target.value } : rest)
                }}
              >
                <option value="">— nothing —</option>
                {WAIT_SURFACES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </NativeSelect>
            </Row>
          </>
        )}
      </div>

      {allowed.length > 1 && (
        <fieldset className="text-xs">
          <legend className="mb-1 font-medium text-foreground">Levels offered</legend>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {allowed.map((level) => (
              <label key={level} className="inline-flex items-center gap-1.5 text-foreground">
                <input
                  type="checkbox"
                  checked={offered.includes(level)}
                  onChange={(e) => toggleLevel(level, e.target.checked)}
                />
                {level}
              </label>
            ))}
          </div>
        </fieldset>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1 text-xs">
          <span className="font-medium text-foreground">Needs</span>
          <TagInput
            ariaLabel="Needs"
            value={step.needs ?? []}
            onChange={(next) => patch({ needs: next })}
            suggestions={fieldIds}
            normalize={(raw) => raw.trim()}
            maxTags={200}
            placeholder="Add a field id and press Enter"
          />
        </div>
        <div className="space-y-1 text-xs">
          <span className="font-medium text-foreground">Produces</span>
          <TagInput
            ariaLabel="Produces"
            value={step.produces ?? []}
            onChange={(next) => patch({ produces: next })}
            suggestions={fieldIds}
            normalize={(raw) => raw.trim()}
            maxTags={200}
            placeholder="Add a field id and press Enter"
          />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Row label="Timeout">
          <NativeSelect aria-label="Timeout" value={timeoutMode} onChange={(e) => setTimeoutMode(e.target.value)}>
            <option value="none">none</option>
            <option value="after">after a duration</option>
            <option value="from_field">when a field's time passes</option>
          </NativeSelect>
        </Row>
        {timeout && (
          <Row label="Timeout takes the exit">
            <NativeSelect
              aria-label="Timeout takes the exit"
              value={timeout.exit}
              onChange={(e) => patch({ timeout: { ...timeout, exit: e.target.value } })}
            >
              {!exitNames.includes(timeout.exit) && <option value={timeout.exit}>{timeout.exit || '— choose —'}</option>}
              {exitNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </NativeSelect>
          </Row>
        )}
        {timeout && timeoutMode === 'after' && (
          <>
            {/* Whole-object writes keep a half-typed duration in the draft:
             * deleting the key would flip the mode and drop this very input. */}
            <Row label="Timeout after">
              <Input
                aria-label="Timeout after"
                placeholder="e.g. 4h, 1d"
                value={timeout.after ?? ''}
                onChange={(e) => patch({ timeout: { ...timeout, after: e.target.value } })}
              />
            </Row>
            <Row label="Timeout calendar">
              <NativeSelect
                aria-label="Timeout calendar"
                value={timeout.calendar ?? 'clock'}
                onChange={(e) => patch({ timeout: { ...timeout, calendar: e.target.value as 'clock' | 'business' } })}
              >
                <option value="clock">clock</option>
                <option value="business">business</option>
              </NativeSelect>
            </Row>
          </>
        )}
        {timeout && timeoutMode === 'from_field' && (
          <Row label="Timeout field">
            <NativeSelect
              aria-label="Timeout field"
              value={timeout.from_field ?? ''}
              onChange={(e) => patch({ timeout: { from_field: e.target.value, exit: timeout.exit } })}
            >
              {!datetimeFields.includes(timeout.from_field ?? '') && (
                <option value={timeout.from_field ?? ''}>{timeout.from_field || '— no datetime field —'}</option>
              )}
              {datetimeFields.map((f) => (
                <option key={f} value={f}>
                  {doc.fields[f]?.label?.en ?? f}
                </option>
              ))}
            </NativeSelect>
          </Row>
        )}
      </div>
    </fieldset>
  )
}
